/**
 * FDR invites for an FDR-scoped CAP. Compliance staff use this to invite an
 * FDR contact (vendor, delegate) to a read-mostly portal view of THIS CAP only.
 *
 * Visible only when cap.is_fdr_scope === true. The actual portal lives at
 * cap-portal.precisioncompliancegroup.com and uses a separate auth surface;
 * this UI just creates the magic-link invite via /api/pcap/caps/:id/fdr-invites.
 */

import React, { useEffect, useState, useCallback } from 'react';
import SectionCard from './SectionCard';
import Pill from './Pill';

const API = process.env.REACT_APP_API_URL;

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function inviteState(inv) {
  if (inv.revoked_at) return { label: 'Revoked', tone: 'bg-slate-100 text-slate-600 ring-slate-200' };
  if (inv.used_at)    return { label: 'Accepted', tone: 'bg-emerald-50 text-emerald-800 ring-emerald-200' };
  if (new Date(inv.expires_at) < new Date()) return { label: 'Expired', tone: 'bg-slate-100 text-slate-500 ring-slate-200' };
  return { label: 'Pending', tone: 'bg-amber-50 text-amber-800 ring-amber-200' };
}

export default function FdrInviteSection({ data, onRefresh }) {
  const cap = data?.cap;
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!cap?.id) return;
    try {
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}/fdr-invites`, { credentials: 'include' });
      const d = await res.json();
      if (d.success) setInvites(d.invites || []);
    } finally { setLoading(false); }
  }, [cap?.id]);

  useEffect(() => { load(); }, [load]);

  if (!cap?.is_fdr_scope) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}/fdr-invites`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), invited_name: name.trim() || null }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Failed to send invite');
      setEmail(''); setName('');
      await load();
      onRefresh?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (inviteId) => {
    if (!window.confirm('Revoke this invite? The link will stop working.')) return;
    try {
      const res = await fetch(`${API}/api/pcap/fdr-invites/${inviteId}/revoke`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Failed to revoke');
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const revokeUser = async (externalUserId) => {
    if (!window.confirm("Revoke this FDR user's access to this CAP?")) return;
    try {
      const res = await fetch(`${API}/api/pcap/fdr-users/${externalUserId}/revoke`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Failed to revoke');
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <SectionCard
      title="FDR access"
      hint="Invite the vendor or delegate. They sign in to a separate portal and only see this one CAP."
    >
      {error && (
        <div className="mb-3 rounded-lg bg-red-50 ring-1 ring-red-200 p-2.5 text-sm text-red-800">{error}</div>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 mb-4">
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="vendor.contact@example.com"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          required
        />
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Contact name (optional)"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        />
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          className="px-3.5 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition disabled:opacity-50"
        >
          {submitting ? 'Sending…' : 'Send invite'}
        </button>
      </form>

      {loading && (
        <p className="text-sm text-slate-400 italic">Loading…</p>
      )}
      {!loading && invites.length === 0 && (
        <p className="text-sm text-slate-400 italic">No invites yet.</p>
      )}
      {!loading && invites.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {invites.map(inv => {
            const s = inviteState(inv);
            const canRevoke = !inv.revoked_at && !inv.used_at && new Date(inv.expires_at) > new Date();
            const userActive = inv.external_user_id && !inv.revoked_at && inv.external_status === 'active';
            return (
              <li key={inv.id} className="py-3 first:pt-0 last:pb-0 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Pill tone={s.tone}>{s.label}</Pill>
                    <span className="text-sm font-medium text-slate-800 truncate">
                      {inv.external_full_name || inv.invited_name || inv.email}
                    </span>
                  </div>
                  <p className="text-[12px] text-slate-500">
                    {inv.email}
                    {' · '}Sent {fmtDate(inv.created_at)}
                    {' · '}Expires {fmtDate(inv.expires_at)}
                    {inv.last_seen_at && <> · Last seen {fmtDate(inv.last_seen_at)}</>}
                  </p>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  {canRevoke && (
                    <button
                      type="button"
                      onClick={() => revoke(inv.id)}
                      className="text-[12px] text-slate-500 hover:text-red-700 underline underline-offset-2"
                    >
                      Revoke invite
                    </button>
                  )}
                  {userActive && (
                    <button
                      type="button"
                      onClick={() => revokeUser(inv.external_user_id)}
                      className="text-[12px] text-slate-500 hover:text-red-700 underline underline-offset-2"
                    >
                      Revoke access
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}
